import { Injectable, inject } from '@angular/core';
import { MatSnackBar, type MatSnackBarConfig } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../../shared/components/snackbar/snackbar.component';
import type { SnackbarData } from '../../shared/models';

@Injectable({
  providedIn: 'root'
})
export class SnackbarService {
  private snackBar = inject(MatSnackBar);

  private readonly defaultConfig: MatSnackBarConfig = {
    duration: 5000,
    horizontalPosition: 'end',
    verticalPosition: 'top',
    panelClass: ['modern-snackbar']
  };

  /**
   * Show a success message
   * @param message - The success message to display
   * @param duration - Optional duration in milliseconds (default: 5000)
   */
  showSuccess(message: string, duration?: number): void {
    const config: MatSnackBarConfig<SnackbarData> = {
      ...this.defaultConfig,
      duration: duration ?? this.defaultConfig.duration,
      data: { message, type: 'success' },
      panelClass: ['modern-snackbar', 'success-snackbar']
    };

    this.snackBar.openFromComponent(SnackbarComponent, config);
  }

  /**
   * Show an error message
   * @param message - The error message to display
   * @param duration - Optional duration in milliseconds (default: 7000 for errors)
   */
  showError(message: string, duration?: number): void {
    const config: MatSnackBarConfig<SnackbarData> = {
      ...this.defaultConfig,
      duration: duration ?? 7000, // Errors stay longer
      data: { message, type: 'error' },
      panelClass: ['modern-snackbar', 'error-snackbar']
    };

    this.snackBar.openFromComponent(SnackbarComponent, config);
  }

  /**
   * Show an info message
   * @param message - The info message to display
   * @param duration - Optional duration in milliseconds (default: 5000)
   */
  showInfo(message: string, duration?: number): void {
    const config: MatSnackBarConfig = {
      ...this.defaultConfig,
      duration: duration ?? this.defaultConfig.duration,
      panelClass: ['modern-snackbar', 'info-snackbar']
    };

    this.snackBar.open(message, 'Close', config);
  }

  /**
   * Show a warning message
   * @param message - The warning message to display
   * @param duration - Optional duration in milliseconds (default: 6000)
   */
  showWarning(message: string, duration?: number): void {
    const config: MatSnackBarConfig<SnackbarData> = {
      ...this.defaultConfig,
      duration: duration ?? 6000,
      data: { message, type: 'warning' },
      panelClass: ['modern-snackbar', 'warning-snackbar']
    };

    this.snackBar.openFromComponent(SnackbarComponent, config);
  }

  /**
   * Dismiss any currently open snackbar
   */
  dismiss(): void {
    this.snackBar.dismiss();
  }
}
